/** Complete-resource leases. Only free resources are retained strongly here;
 * live owners keep their own leases. Overflow never evicts an in-use resource.
 * Unknown/exhausted leases cannot be released twice. */
export class BoundedResourcePool {
  constructor({ maxKeys, maxFreePerKey, maxFree, dispose }) {
    this.limits = { maxKeys, maxFreePerKey, maxFree };
    this.dispose = dispose;
    this.buckets = new Map();
    this.leases = new WeakMap();
    this.counts = { allocations: 0, reuses: 0, releases: 0, disposals: 0, live: 0, free: 0 };
  }

  acquire(key, create) {
    const bucket = this.buckets.get(key);
    let resource = bucket?.pop();
    if (resource) {
      this.counts.free--;
      this.counts.reuses++;
      if (bucket.length === 0) this.buckets.delete(key);
    } else {
      resource = create();
      this.counts.allocations++;
    }
    this.leases.set(resource, { key, live: true });
    this.counts.live++;
    return resource;
  }

  release(resource) {
    const lease = this.leases.get(resource);
    if (!lease?.live) return false;
    lease.live = false;
    this.counts.live--;
    this.counts.releases++;
    const bucket = this.buckets.get(lease.key);
    const { maxKeys, maxFreePerKey, maxFree } = this.limits;
    if (this.counts.free >= maxFree || (bucket?.length ?? 0) >= maxFreePerKey ||
        (!bucket && this.buckets.size >= maxKeys)) {
      this.counts.disposals++;
      this.dispose(resource);
    } else {
      if (bucket) bucket.push(resource);
      else this.buckets.set(lease.key, [resource]);
      this.counts.free++;
    }
    return true;
  }

  stats() {
    return { ...this.counts, keys: this.buckets.size, limits: { ...this.limits } };
  }
}
